<template>
  <div class="row align-items-center mb-4">
    <div class="col-md-2 d-flex align-items-center">
      <label for="calendar-date" class="form-label me-2 mt-2">Data</label>
      <Calendar id="calendar-date" class="fw-bold" v-model="baseDate"
                dateFormat="dd/mm/yy" :min-date="minDate"
                @date-select="changeBaseDate"
      />
    </div>
  </div>

  <div class="col-md-12 mb-6" v-if="professional?.id_prof">
    <div class="mt-3">
      <!-- Headers fixos -->
      <div class="schedule-headers">
        <div class="time-column-header">
          <div class="time-header">Horário</div>
        </div>
        <div class="specialty-headers-row" ref="headersRow">
          <div v-for="spec in uniqueSpecialties" :key="spec.id_spec" class="spec-column-header">
            <div class="spec-header">{{ spec.spec_name }}</div>
          </div>
        </div>
      </div>

      <!-- Container com scroll -->
      <div class="schedule-container" ref="scheduleContainer" @scroll="onScheduleScroll">
        <!-- Coluna de horários -->
        <div class="time-column">
          <div v-for="hour in hours" :key="hour" class="time-slot" :style="{ height: slotHeight + 'px' }">
            {{ hour }}
          </div>
        </div>

        <!-- Colunas de especialidades -->
        <div v-for="spec in uniqueSpecialties" :key="spec.id_spec" class="specialty-column">
          <div class="spec-schedule" :style="{ minHeight: totalHeight + 'px' }">
            <div v-for="avail in getAvailabilitiesBySpec(spec.id_spec)"
                 :key="`${avail.start_datetime}-${avail.end_datetime}`"
                 class="availability-block"
                 :style="getBlockStyle(avail)">
              <div class="block-content">
                <div v-if="avail.max_avail_online" class="badge bg-primary">Teleconsulta</div>
                <div v-if="!avail.min_avail_online" class="badge bg-success">Presencial</div>
                <div v-if="avail.allow_return" class="badge bg-info">Permite Retorno</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else>
    <p class="text-center">Nenhuma informação disponível</p>
  </div>
</template>

<script>
import http from "../../../../helpers/http";
import Calendar from "primevue/calendar";

export default {
  name: "AvailTime",
  components: {
    Calendar,
  },
  emits: ["onRefreshed"],
  props: {
    professional: null,
  },
  data() {
    return {
      availabilitySummary: [],
      baseDate: new Date(),
      minDate: new Date(),
      slotHeight: 60,
    }
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
    uniqueSpecialties() {
      const specs = {};
      this.availabilitySummary.forEach(item => {
        if (!specs[item.id_spec]) {
          specs[item.id_spec] = {
            id_spec: item.id_spec,
            spec_name: item.spec_name
          };
        }
      });
      return Object.values(specs).sort((a, b) =>
          a.spec_name.localeCompare(b.spec_name)
      );
    },
    hours() {
      if (this.availabilitySummary.length === 0) return [];

      let minHour = 24;
      let maxHour = 0;

      this.availabilitySummary.forEach(item => {
        const startHour = parseInt(item.start_datetime.substring(0, 2));
        const endHour = parseInt(item.end_datetime.substring(0, 2));

        if (startHour < minHour) minHour = startHour;
        if (endHour > maxHour) maxHour = endHour;
      });

      const hoursArray = [];
      for (let h = minHour; h <= maxHour; h++) {
        hoursArray.push(`${String(h).padStart(2, '0')}:00`);
      }

      return hoursArray;
    },
    totalHeight() {
      if (this.hours.length === 0) return 0;
      const startHour = parseInt(this.hours[0].split(':')[0]);
      const endHour = parseInt(this.hours[this.hours.length - 1].split(':')[0]) + 1;
      return (endHour - startHour) * this.slotHeight;
    },
  },
  watch: {
    refresh(newvalue) {
      if ((newvalue ?? false) && this.professional) {
        this.getSummary();
        this.$emit('onRefreshed');
      }
    },
  },
  methods: {
    getAvailabilitiesBySpec(specId) {
      return this.availabilitySummary.filter(item => item.id_spec === specId);
    },
    getBlockStyle(availability) {
      const startTime = availability.start_datetime;
      const endTime = availability.end_datetime;

      const [startHour, startMin] = startTime.split(':').map(Number);
      const [endHour, endMin] = endTime.split(':').map(Number);

      const minStartHour = parseInt(this.hours[0].split(':')[0]);

      const topOffset = ((startHour - minStartHour) * 60 + startMin) * (this.slotHeight / 60);
      const duration = ((endHour - startHour) * 60 + (endMin - startMin));
      const height = (duration / 60) * this.slotHeight;

      return {
        top: topOffset + 'px',
        height: height + 'px',
      };
    },
    onScheduleScroll() {
      if (this.$refs.scheduleContainer && this.$refs.headersRow) {
        this.$refs.headersRow.scrollLeft = this.$refs.scheduleContainer.scrollLeft;
      }
    },
    getSummary() {
      this.availabilitySummary = [];

      const params = {
        date_begin: moment(this.baseDate).format('YYYY-MM-DD'),
        date_end: moment(this.baseDate).format('YYYY-MM-DD'),
        id_prof: this.professional?.id_prof,
      };

      http.get(`/places/${this.place.id_place}/professionals/${this.professional?.id_prof}/availabilities/summary`, {
        params: params
      }).then((response) => {
        this.availabilitySummary = response.data;
      }).catch((e) => {
        console.error('Erro ao carregar disponibilidades:', e);
      });
    },
    changeBaseDate() {
      this.getSummary();
    },
  },
  mounted() {
    if (this.professional?.id_prof) {
      this.getSummary();
    }
  }
}
</script>

<style scoped>
.schedule-headers {
  display: flex;
  border: 1px solid #ddd;
  border-bottom: none;
  border-radius: 0.375rem 0.375rem 0 0;
  background-color: #f8f9fa;
  position: relative;
}

.time-column-header {
  width: 100px;
  flex-shrink: 0;
  border-right: 1px solid #eee;
  position: sticky;
  left: 0;
  z-index: 2;
  background-color: #f8f9fa;
}

.spec-column-header {
  width: 250px;
  flex-shrink: 0;
  border-right: 1px solid #eee;
}

.time-header, .spec-header {
  font-weight: bold;
  padding: 0.75rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #ddd;
  text-align: center;
  font-size: 0.875rem;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.schedule-container {
  display: flex;
  //gap: 0.2rem;
  border: 1px solid #ddd;
  border-radius: 0 0 0.375rem 0.375rem;
  overflow-x: auto;
  overflow-y: auto;
  max-height: 400px;
  position: relative;
}

.time-column {
  width: 100px;
  flex-shrink: 0;
  background-color: white;
  position: sticky;
  left: 0;
  z-index: 1;
  border-right: 1px solid #eee;
}

.specialty-column {
  width: 250px;
  flex-shrink: 0;
  position: relative;
  border-right: 1px solid #eee;
  margin-left: 0;
}

.time-slot {
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #eee;
  font-size: 0.75rem;
  color: #666;
  height: 60px;
  background-color: white;
}

.spec-schedule {
  position: relative;
  background-color: #fafafa;
  width: 100%;
}

.availability-block {
  position: absolute;
  left: 0.5rem;
  right: 0.5rem;
  border-radius: 0.375rem;
  background-color: #e7f3ff;
  border: 1px solid #b3d9ff;
  padding: 0.5rem;
  overflow: hidden;
}

.block-content {
  display: flex;
  flex-direction: row;
  gap: 0.25rem;
  flex-wrap: wrap;
}

.badge {
  display: inline-block;
  border-radius: 0.25rem;
  font-size: 0.625rem;
  padding: 0.25rem 0.375rem;
  text-align: center;
}

.schedule-container::-webkit-scrollbar {
  width: 16px;
  height: 16px;
}

.schedule-container::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.schedule-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 8px;
}

.schedule-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.schedule-container {
  scrollbar-width: auto;
  scrollbar-color: #c1c1c1 #f1f1f1;
}

.specialty-headers-row {
  display: flex;
  gap: 0;
  flex: 1;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding-right: 50px;
}

.specialty-headers-row::-webkit-scrollbar {
  display: none;
}

</style>